import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowRight,
  ClipboardList,
  MapPin,
  RotateCcw,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { JobCard } from "@/components/jobs/job-card";
import { SectionHeading } from "@/components/section-heading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/lib/i18n";
import { getPublicJobs } from "@/lib/jobs.functions";
import { JOB_CATEGORIES, type JobFilters, type JobPublicRow, type JobSort } from "@/lib/jobs.types";

type JobsSearch = {
  q?: string;
  category?: string;
  city?: string;
  sort?: JobSort;
};

export const Route = createFileRoute("/auftraege")({
  staticData: { sitemap: true },
  validateSearch: (search: Record<string, unknown>): JobsSearch => ({
    q: typeof search.q === "string" ? search.q.slice(0, 120) : undefined,
    category: typeof search.category === "string" ? search.category.slice(0, 60) : undefined,
    city: typeof search.city === "string" ? search.city.slice(0, 100) : undefined,
    sort: search.sort === "oldest" ? "oldest" : search.sort === "newest" ? "newest" : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Aktuelle Aufträge für Auftragnehmer. Begner" },
      {
        name: "description",
        content: "Geprüfte Bau-, Handwerks- und Dienstleistungsaufträge aus der Schweiz entdecken.",
      },
      { property: "og:title", content: "Aktuelle Aufträge. Begner" },
      {
        property: "og:description",
        content: "Neue, geprüfte Projekte aus Ihrer Region entdecken.",
      },
    ],
  }),
  component: JobsRoute,
});

const overviewCopy = {
  de: {
    eyebrow: "Auftragsmarkt",
    title: "Projekte, die auf den richtigen Profi warten.",
    intro:
      "Durchsuchen Sie geprüfte Aufträge aus der Schweiz. Die Beschreibung bleibt im Original, damit kein wichtiges Detail verloren geht.",
    post: "Anfrage eingeben",
    search: "Wonach suchen Sie?",
    searchPlaceholder: "z. B. Badumbau, Reinigung oder Garten",
    category: "Kategorie",
    allCategories: "Alle Kategorien",
    city: "Ort",
    cityPlaceholder: "z. B. Zürich",
    sort: "Sortierung",
    newest: "Neueste zuerst",
    oldest: "Älteste zuerst",
    apply: "Aufträge anzeigen",
    reset: "Zurücksetzen",
    results: "geprüfte Aufträge",
    oneResult: "geprüfter Auftrag",
    loading: "Aufträge werden geladen",
    emptyTitle: "Keine passenden Aufträge gefunden.",
    emptyText: "Ändern Sie die Suche oder setzen Sie die Filter zurück.",
    errorTitle: "Die Aufträge konnten gerade nicht geladen werden.",
    errorText: "Bitte versuchen Sie es in einem Moment erneut.",
    retry: "Erneut versuchen",
  },
  fr: {
    eyebrow: "Marché des mandats",
    title: "Des projets qui attendent le bon professionnel.",
    intro:
      "Parcourez les mandats vérifiés en Suisse. La description reste dans sa langue d'origine afin de préserver chaque détail.",
    post: "Publier son propre mandat",
    search: "Que recherchez-vous ?",
    searchPlaceholder: "p. ex. salle de bains, nettoyage ou jardin",
    category: "Catégorie",
    allCategories: "Toutes les catégories",
    city: "Localité",
    cityPlaceholder: "p. ex. Zurich",
    sort: "Tri",
    newest: "Plus récents d'abord",
    oldest: "Plus anciens d'abord",
    apply: "Afficher les mandats",
    reset: "Réinitialiser",
    results: "mandats vérifiés",
    oneResult: "mandat vérifié",
    loading: "Chargement des mandats",
    emptyTitle: "Aucun mandat correspondant.",
    emptyText: "Modifiez votre recherche ou réinitialisez les filtres.",
    errorTitle: "Impossible de charger les mandats pour le moment.",
    errorText: "Veuillez réessayer dans quelques instants.",
    retry: "Réessayer",
  },
  it: {
    eyebrow: "Mercato degli incarichi",
    title: "Progetti che aspettano il professionista giusto.",
    intro:
      "Cerchi tra gli incarichi verificati in Svizzera. La descrizione resta nella lingua originale per non perdere alcun dettaglio.",
    post: "Pubblica il suo incarico",
    search: "Cosa sta cercando?",
    searchPlaceholder: "es. bagno, pulizia o giardino",
    category: "Categoria",
    allCategories: "Tutte le categorie",
    city: "Località",
    cityPlaceholder: "es. Zurigo",
    sort: "Ordine",
    newest: "Più recenti prima",
    oldest: "Meno recenti prima",
    apply: "Mostra incarichi",
    reset: "Reimposta",
    results: "incarichi verificati",
    oneResult: "incarico verificato",
    loading: "Caricamento incarichi",
    emptyTitle: "Nessun incarico corrispondente.",
    emptyText: "Modifichi la ricerca o reimposti i filtri.",
    errorTitle: "Al momento non è possibile caricare gli incarichi.",
    errorText: "Riprovi tra qualche istante.",
    retry: "Riprova",
  },
  en: {
    eyebrow: "Job marketplace",
    title: "Projects waiting for the right professional.",
    intro:
      "Browse verified jobs from across Switzerland. Descriptions remain in their original language so no important detail is lost.",
    post: "Post your own job",
    search: "What are you looking for?",
    searchPlaceholder: "e.g. bathroom renovation, cleaning or garden",
    category: "Category",
    allCategories: "All categories",
    city: "Location",
    cityPlaceholder: "e.g. Zurich",
    sort: "Sort",
    newest: "Newest first",
    oldest: "Oldest first",
    apply: "Show jobs",
    reset: "Reset",
    results: "verified jobs",
    oneResult: "verified job",
    loading: "Loading jobs",
    emptyTitle: "No matching jobs found.",
    emptyText: "Change your search or reset the filters.",
    errorTitle: "Jobs could not be loaded right now.",
    errorText: "Please try again in a moment.",
    retry: "Try again",
  },
} as const;

function JobsRoute() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return pathname.startsWith("/auftraege/") ? <Outlet /> : <JobsOverview />;
}

function JobsOverview() {
  const { language } = useLanguage();
  const copy = overviewCopy[language];
  const search = Route.useSearch();
  const navigate = useNavigate();
  const loadJobs = useServerFn(getPublicJobs);
  const initialCategory = JOB_CATEGORIES.some((item) => item.id === search.category)
    ? search.category!
    : "all";
  const [query, setQuery] = useState(search.q ?? "");
  const [category, setCategory] = useState(initialCategory);
  const [city, setCity] = useState(search.city ?? "");
  const [sort, setSort] = useState<JobSort>(search.sort ?? "newest");
  const [applied, setApplied] = useState<JobFilters>({
    q: search.q,
    category: initialCategory === "all" ? undefined : initialCategory,
    city: search.city,
    sort: search.sort ?? "newest",
  });
  const [jobs, setJobs] = useState<JobPublicRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    void loadJobs({ data: applied })
      .then((result) => {
        if (active) setJobs(result.jobs);
      })
      .catch(() => {
        if (!active) return;
        setJobs([]);
        setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [applied, loadJobs, retryKey]);

  const applyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next: JobsSearch = {
      q: query.trim() || undefined,
      category: category === "all" ? undefined : category,
      city: city.trim() || undefined,
      sort,
    };
    setApplied(next);
    void navigate({ to: "/auftraege", search: next, replace: true });
  };

  const resetFilters = () => {
    setQuery("");
    setCategory("all");
    setCity("");
    setSort("newest");
    setApplied({ sort: "newest" });
    void navigate({ to: "/auftraege", search: { sort: "newest" }, replace: true });
  };

  return (
    <>
      <section className="relative overflow-hidden border-b border-border bg-surface">
        <div
          aria-hidden
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 15%, color-mix(in oklab, var(--brand) 18%, transparent), transparent 45%), radial-gradient(circle at 8% 95%, color-mix(in oklab, var(--brand-glow) 18%, transparent), transparent 48%)",
          }}
        />
        <div className="container-page relative py-12 md:py-16">
          <div className="flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading
              eyebrow={copy.eyebrow}
              title={copy.title}
              description={copy.intro}
              level="page"
              className="max-w-2xl"
              descriptionClassName="text-base leading-relaxed"
            />
            <Button asChild size="lg" className="h-12 w-fit rounded-xl shadow-brand">
              <Link to="/auftrag-einstellen">
                {copy.post}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-background py-8 md:py-10">
        <div className="container-page">
          <form
            onSubmit={applyFilters}
            className="relative z-10 grid gap-3 rounded-2xl border border-border bg-card p-4 shadow-card md:grid-cols-2 lg:grid-cols-[1.35fr_1fr_1fr_0.85fr_auto] lg:items-end"
          >
            <div className="grid grid-rows-[auto_2.75rem] gap-2 md:col-span-2 lg:col-span-1">
              <Label htmlFor="job-search">{copy.search}</Label>
              <div className="relative h-11">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="job-search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={copy.searchPlaceholder}
                  maxLength={120}
                  className="h-11 rounded-xl pl-10"
                />
              </div>
            </div>
            <div className="grid grid-rows-[auto_2.75rem] gap-2">
              <Label htmlFor="job-category">{copy.category}</Label>
              <div className="h-11">
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger id="job-category" className="h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{copy.allCategories}</SelectItem>
                    {JOB_CATEGORIES.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.label[language]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-rows-[auto_2.75rem] gap-2">
              <Label htmlFor="job-city">{copy.city}</Label>
              <div className="relative h-11">
                <MapPin className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="job-city"
                  value={city}
                  onChange={(event) => setCity(event.target.value)}
                  placeholder={copy.cityPlaceholder}
                  maxLength={100}
                  className="h-11 rounded-xl pl-10"
                />
              </div>
            </div>
            <div className="grid grid-rows-[auto_2.75rem] gap-2">
              <Label htmlFor="job-sort">{copy.sort}</Label>
              <div className="h-11">
                <Select value={sort} onValueChange={(value) => setSort(value as JobSort)}>
                  <SelectTrigger id="job-sort" className="h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">{copy.newest}</SelectItem>
                    <SelectItem value="oldest">{copy.oldest}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="h-11 rounded-xl px-5">
              <SlidersHorizontal className="h-4 w-4" />
              {copy.apply}
            </Button>
          </form>

          <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {loading ? (
                copy.loading
              ) : (
                <>
                  <span className="font-semibold text-foreground">{jobs.length}</span>{" "}
                  {jobs.length === 1 ? copy.oneResult : copy.results}
                </>
              )}
            </p>
            {(applied.q || applied.category || applied.city || applied.sort === "oldest") && (
              <Button type="button" variant="ghost" size="sm" onClick={resetFilters}>
                <RotateCcw className="h-4 w-4" />
                {copy.reset}
              </Button>
            )}
          </div>

          {loading ? (
            <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <div
                  key={index}
                  className="overflow-hidden rounded-2xl border border-border bg-card"
                >
                  <div className="aspect-[16/7] animate-pulse bg-muted" />
                  <div className="space-y-3 p-4">
                    <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
                    <div className="h-7 w-4/5 animate-pulse rounded bg-muted" />
                    <div className="h-16 animate-pulse rounded bg-muted/70" />
                  </div>
                </div>
              ))}
            </div>
          ) : jobs.length ? (
            <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {jobs.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>
          ) : (
            <div className="mt-5 flex flex-col items-center rounded-3xl border border-dashed border-border bg-surface px-6 py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-soft text-brand-deep">
                <ClipboardList className="h-7 w-7" />
              </div>
              <h2 className="mt-5 font-display text-2xl font-bold">
                {failed ? copy.errorTitle : copy.emptyTitle}
              </h2>
              <p className="mt-2 max-w-lg text-sm text-muted-foreground">
                {failed ? copy.errorText : copy.emptyText}
              </p>
              <Button
                variant="outline"
                className="mt-6 rounded-xl"
                onClick={failed ? () => setRetryKey((current) => current + 1) : resetFilters}
              >
                <RotateCcw className="h-4 w-4" />
                {failed ? copy.retry : copy.reset}
              </Button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
